import React from 'react';
import './SubscriptionUsageIndicator.css';

// Module Feature Keys & Label Mapping matching exact Figma reference wording
const MODULE_CONFIG = {
  invoices: {
    featureKeys: ['invoices_per_month', 'invoices', 'invoice'],
    label: 'Invoices Created',
  },
  purchaseOrders: {
    featureKeys: ['purchase_orders_per_month', 'purchase_orders', 'purchaseOrders'],
    label: 'Orders Created',
  },
  purchaseBills: {
    featureKeys: ['purchase_bills_per_month', 'purchase_bills', 'bills'],
    label: 'Purchase Bills Created',
  },
  products: {
    featureKeys: ['products_limit', 'product_per_month', 'product'],
    label: 'Products Created',
  },
  users: {
    featureKeys: ['users', 'users_per_month', 'active_users'],
    label: 'Users',
  },
};
// purchase_bills_per_month
const THRESHOLD_COLORS = {
  normal: '#22C55E',       // Green (<50%)
  info: '#0EA5E9',         // Sky Blue / Cyan (50-74%)
  warning: '#F59E0B',      // Yellow (75-89%)
  highPriority: '#F97316', // Orange (90-99%)
  limitReached: '#EF4444', // Red (>=100%)
};

// Renders subscription usage indicator progress bar with dynamic threshold colors
export const SubscriptionUsageIndicator = ({ usageData, moduleKey, className = '' }) => {
  const config = MODULE_CONFIG[moduleKey];
  if (!config || !usageData) return null;

  // Find matching feature key in usage payload
  let feature = null;
  for (const key of config.featureKeys) {
    if (usageData[key] !== undefined && usageData[key] !== null) {
      feature = usageData[key];
      break;
    }
  }

  if (!feature) return null;

  const isUnlimited =
    feature.total === 'unlimited' ||
    feature.total_allowed === 'unlimited' ||
    feature.is_unlimited === true;

  const consumed = feature.consumed ?? feature.total_consumed ?? 0;
  const total = isUnlimited ? 'Unlimited' : (feature.total ?? feature.total_allowed ?? 0);

  let rawPercentage = 0;
  if (!isUnlimited) {
    if (feature.usage_percentage !== undefined && feature.usage_percentage !== null) {
      rawPercentage = Number(feature.usage_percentage);
    } else if (Number(total) > 0) {
      rawPercentage = Math.round((Number(consumed) / Number(total)) * 100);
    } else if (Number(consumed) > 0) {
      rawPercentage = 100;
    }
  }

  const fillPercentage = Math.min(100, Math.max(0, rawPercentage));

  // Determine visual threshold state
  let thresholdState = 'normal';
  if (!isUnlimited) {
    if (rawPercentage >= 100) thresholdState = 'limitReached';
    else if (rawPercentage >= 90) thresholdState = 'highPriority';
    else if (rawPercentage >= 75) thresholdState = 'warning';
    else if (rawPercentage >= 50) thresholdState = 'info';
  }

  const displayText =
    rawPercentage >= 100 && !isUnlimited
      ? `${consumed} / ${total} ${config.label} (Limit Reached)`
      : `${consumed} / ${total} ${config.label}`;

  const barColor = THRESHOLD_COLORS[thresholdState] || THRESHOLD_COLORS.normal;

  return (
    <div className={`sub-usage-indicator ${thresholdState} ${isUnlimited ? 'unlimited' : ''} ${className}`}>
      <div className="sub-usage-text-row">
        <span className="sub-usage-text">{displayText}</span>
      </div>
      <div className="sub-usage-track">
        {!isUnlimited && (
          <div
            className="sub-usage-bar"
            style={{
              width: `${fillPercentage}%`,
              backgroundColor: barColor,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SubscriptionUsageIndicator;
